/* ==========================================================================
   OrangeWear — global.js
   Shared helpers on window.theme. Loaded with defer on every page.
   See ARCHITECTURE.md §5 for the public API.
   ========================================================================== */
(() => {
  const theme = (window.theme = window.theme || {});
  theme.routes = theme.routes || {};
  theme.settings = theme.settings || {};
  theme.strings = theme.strings || {};

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Utilities ---------- */
  theme.debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  theme.on = (name, handler) => {
    const listener = (event) => handler(event.detail || {});
    document.addEventListener(`theme:${name}`, listener);
    return () => document.removeEventListener(`theme:${name}`, listener);
  };

  theme.emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`theme:${name}`, { detail }));
  };

  theme.fetchJSON = async (url, options = {}) => {
    const headers = { Accept: 'application/json', ...(options.headers || {}) };
    if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await fetch(url, { ...options, headers });
    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }
    if (!response.ok) {
      const message = (data && (data.description || data.message)) || response.statusText || theme.strings.cartError;
      const error = new Error(typeof message === 'string' ? message : theme.strings.cartError);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  };

  theme.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  theme.fetchSectionHTML = async (url, sectionId) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target.toString(), { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(response.statusText);
    return response.text();
  };

  theme.replaceContent = (sourceDoc, selector, root = document) => {
    const fresh = sourceDoc.querySelector(selector);
    const current = root.querySelector(selector);
    if (!fresh || !current) return false;
    current.innerHTML = fresh.innerHTML;
    return true;
  };

  theme.formatMoney = (cents, format) => {
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : Number(cents || 0);
    const moneyFormat = format || (theme.settings.showCurrencyCode ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat) || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;

    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? grouped + decimal + fraction : grouped;
    };

    const match = moneyFormat.match(placeholder);
    if (!match) return moneyFormat;
    let formatted;
    switch (match[1]) {
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(value, 2, "'", '.');
        break;
      default:
        formatted = withDelimiters(value, 2);
    }
    return moneyFormat.replace(placeholder, formatted);
  };

  /* ---------- Cart ---------- */
  const cartSections = new Map([
    ['cart-drawer', '#CartDrawer-Content'],
    ['cart-icon-bubble', '#cart-icon-bubble'],
  ]);

  const toFormData = (object) => {
    const formData = new FormData();
    Object.entries(object || {}).forEach(([key, value]) => formData.append(key, value));
    return formData;
  };

  theme.cart = {
    registerSection(sectionId, selector) {
      cartSections.set(sectionId, selector);
    },

    unregisterSection(sectionId) {
      cartSections.delete(sectionId);
    },

    sectionIds() {
      return Array.from(cartSections.keys());
    },

    applySections(sections) {
      if (!sections) return;
      cartSections.forEach((selector, sectionId) => {
        const html = sections[sectionId];
        if (!html) return;
        theme.replaceContent(theme.parseHTML(html), selector);
      });
      theme.emit('cart:rendered', {});
    },

    async get() {
      return theme.fetchJSON(`${theme.routes.cart}.js`);
    },

    async add(input) {
      const body = input instanceof FormData ? input : toFormData(input);
      body.set('sections', theme.cart.sectionIds().join(','));
      body.set('sections_url', window.location.pathname);

      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }
      if (!response.ok || !data || data.status) {
        const message = (data && (data.description || data.message)) || theme.strings.cartError;
        const error = new Error(typeof message === 'string' ? message : theme.strings.cartError);
        error.data = data;
        throw error;
      }

      if (theme.settings.cartType === 'page') {
        window.location.href = theme.routes.cart;
        return data;
      }

      theme.cart.applySections(data.sections);
      let cart = null;
      try {
        cart = await theme.cart.get();
      } catch (error) {
        cart = null;
      }
      theme.emit('cart:updated', { cart, source: 'add' });
      theme.emit('cart:item-added', { item: data, cart });
      return data;
    },

    async change(line, quantity) {
      const payload = {
        quantity,
        sections: theme.cart.sectionIds(),
        sections_url: window.location.pathname,
      };
      if (typeof line === 'string') {
        payload.id = line;
      } else {
        payload.line = line;
      }
      const cart = await theme.fetchJSON(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      theme.cart.applySections(cart.sections);
      theme.emit('cart:updated', { cart, source: 'change' });
      return cart;
    },

    async updateNote(note) {
      return theme.fetchJSON(`${theme.routes.cartUpdate}.js`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      });
    },
  };

  /* ---------- Dialogs ---------- */
  const resolveDialog = (dialogOrSelector) =>
    typeof dialogOrSelector === 'string' ? document.querySelector(dialogOrSelector) : dialogOrSelector;

  theme.openDialog = (dialogOrSelector, opener) => {
    const dialog = resolveDialog(dialogOrSelector);
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.classList.remove('is-closing');
    dialog.showModal();
    if (dialog._opener && dialog._opener.setAttribute) {
      dialog._opener.setAttribute('aria-expanded', 'true');
    }
    dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
  };

  theme.closeDialog = (dialogOrSelector) => {
    const dialog = resolveDialog(dialogOrSelector);
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open || dialog.classList.contains('is-closing')) return;
    const finish = () => {
      dialog.classList.remove('is-closing');
      dialog.close();
    };
    if (prefersReducedMotion()) {
      finish();
      return;
    }
    dialog.classList.add('is-closing');
    window.setTimeout(finish, 200);
  };

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('dialog')) return;
      const opener = dialog._opener;
      if (opener && opener.setAttribute) opener.setAttribute('aria-expanded', 'false');
      const openerVisible =
        opener && typeof opener.focus === 'function' && document.contains(opener) && opener.getClientRects().length > 0;
      if (openerVisible) {
        opener.focus({ preventScroll: true });
      } else {
        const main = document.getElementById('MainContent');
        if (main) main.focus({ preventScroll: true });
      }
      dialog.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
    },
    true
  );

  document.addEventListener(
    'cancel',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('dialog')) return;
      event.preventDefault();
      theme.closeDialog(dialog);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.querySelector(opener.getAttribute('data-dialog-open'));
      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        theme.openDialog(dialog, opener);
      }
      return;
    }

    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) {
        event.preventDefault();
        theme.closeDialog(dialog);
      }
      return;
    }

    const target = event.target;
    if (target instanceof HTMLDialogElement && target.classList.contains('dialog') && target.open) {
      const rect = target.getBoundingClientRect();
      const outside =
        event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
      if (outside) theme.closeDialog(target);
    }
  });

  /* ---------- Custom elements ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', this.onButtonClick);
      if (this.input) this.input.addEventListener('change', this.updateButtons);
      this.updateButtons();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onButtonClick);
      if (this.input) this.input.removeEventListener('change', this.updateButtons);
    }

    get bounds() {
      const step = Number(this.input.step) || 1;
      const min = this.input.min !== '' ? Number(this.input.min) : 0;
      const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
      return { step, min, max };
    }

    onButtonClick = (event) => {
      const button = event.target.closest('button[name="plus"], button[name="minus"]');
      if (!button || !this.input) return;
      event.preventDefault();
      const { step, min, max } = this.bounds;
      const current = Number(this.input.value) || min;
      const next = button.name === 'plus' ? Math.min(current + step, max) : Math.max(current - step, min);
      if (next === current) return;
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    updateButtons = () => {
      if (!this.input) return;
      const { min, max } = this.bounds;
      const value = Number(this.input.value);
      const minus = this.querySelector('button[name="minus"]');
      const plus = this.querySelector('button[name="plus"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
    };
  }

  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.onScroll = theme.debounce(() => this.update(), 60);
      if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    get slides() {
      return Array.from(this.track.children).filter((slide) => slide.offsetParent !== null);
    }

    get slideStep() {
      const [first, second] = this.slides;
      if (!first) return this.track.clientWidth;
      if (second) return second.offsetLeft - first.offsetLeft;
      return first.getBoundingClientRect().width;
    }

    get currentIndex() {
      const stepSize = this.slideStep || 1;
      return Math.round(this.track.scrollLeft / stepSize);
    }

    step(direction) {
      this.scrollToIndex(this.currentIndex + direction);
    }

    scrollToIndex(index) {
      const slides = this.slides;
      if (!slides.length) return;
      const clamped = Math.max(0, Math.min(index, slides.length - 1));
      const left = slides[clamped].offsetLeft - slides[0].offsetLeft;
      this.track.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    update() {
      if (!this.track) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      const scrollable = maxScroll > 0;
      this.classList.toggle('is-scrollable', scrollable);
      if (this.prevButton) this.prevButton.disabled = !scrollable || this.track.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = !scrollable || this.track.scrollLeft >= maxScroll;
      this.dispatchEvent(new CustomEvent('slider:change', { detail: { index: this.currentIndex } }));
    }
  }

  if (!customElements.get('slider-component')) customElements.define('slider-component', SliderComponent);

  /* ---------- Rich text tables ---------- */
  const wrapTables = (root = document) => {
    root.querySelectorAll('.rte table').forEach((table) => {
      if (table.parentElement && table.parentElement.classList.contains('table-wrap')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'table-wrap';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  };

  theme.wrapTables = wrapTables;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => wrapTables());
  } else {
    wrapTables();
  }

  document.addEventListener('shopify:section:load', (event) => wrapTables(event.target));
})();
